import { API_ORIGIN, apiUrl } from "./apiOrigin";
import React, { useEffect, useState } from 'react';
import InspectorSOPStaffGrid from './InspectorSOPStaffGrid';
import ComplianceNextDue from './ComplianceNextDue';
import { TrainingSettings, TrainingResults } from './ComplianceTraining';
const API = apiUrl("/api/headoffice/compliance-grid/sop-cascade");
export const isSopCascade = row => String(row?.elementId || row?.rowId || row || '').replace(/^el:/, '') === 'ni-p13-11';
const button = {
  padding: '8px 12px',
  border: '1px solid #94a3b8',
  borderRadius: 6,
  background: '#fff',
  color: '#262626',
  cursor: 'pointer'
};
const date = v => v ? new Date(v).toLocaleDateString('en-GB') : 'Not recorded';
async function api(token, path, body, signal) {
  const r = await fetch(API + path, {
    method: 'POST',
    signal,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token || localStorage.getItem('token') || ''}`
    },
    body: JSON.stringify(body)
  });
  const d = await r.json();
  if (!r.ok) throw Error(d.error || 'Unable to load SOP change communication.');
  return d;
}
async function readFiles(files, kind) {
  return Promise.all([...files].map(async f => {
    if (!f.size || f.size > 8 * 1024 * 1024) throw Error('Each document must be between 1 byte and 8 MB.');
    const dataBase64 = await new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve(String(r.result).split(',')[1]);
      r.onerror = reject;
      r.readAsDataURL(f);
    });
    return {
      filename: f.name,
      dataBase64,
      kind
    };
  }));
}
export function SopCascadeConfig({
  row,
  token,
  onClose,
  onSaved
}) {
  const [data, setData] = useState(null),
    [mode, setMode] = useState('cascade'),
    [chosen, setChosen] = useState([]),
    [titles, setTitles] = useState([]),
    [link, setLink] = useState(''),
    [recipients, setRecipients] = useState({}),
    [attachments, setAttachments] = useState([]),
    [days, setDays] = useState(365),
    [training, setTraining] = useState({}),
    [trainingAttachment, setTrainingAttachment] = useState(null),
    [busy, setBusy] = useState(false),
    [reading, setReading] = useState(false),
    [error, setError] = useState(''),
    [helpOpen, setHelpOpen] = useState('');
  useEffect(() => {
    const c = new AbortController();
    api(token, '/load', {}, c.signal).then(d => {
      if (c.signal.aborted) return;
      setData(d);
      setChosen(d.branches.map(b => b.dbName));
      setMode(d.row.sopCascadeMode || d.cells[0]?.sopCascade?.mode || d.cells[0]?.sopCascadeMode || d.row.defaultSource || 'cascade');
      setTitles(d.row.sopTitles || []);
      setLink(d.cells.find(c => c.linkUrl)?.linkUrl || '');
      setRecipients(Object.fromEntries(d.cells.map(c => [c.dbName, c.recipientStaffId || ''])));
      setDays(d.row.validity?.days || 365);
      setTraining(d.row.training || {});
    }).catch(e => {
      if (!c.signal.aborted) setError(e.message);
    });
    return () => c.abort();
  }, [token]);
  const upload = async (files, kind) => {
    setReading(true);
    setError('');
    try {
      const added = await readFiles(files, kind);
      const next = [...attachments.filter(f => f.kind !== kind), ...added];
      if (next.length > 6 || next.reduce((n, f) => n + f.dataBase64.length * 3 / 4, 0) > 8 * 1024 * 1024) throw Error('Choose up to six documents with a combined size of 8 MB or less.');
      setAttachments(next);
    } catch (e) {
      setError(e.message);
    } finally {
      setReading(false);
    }
  };
  const save = async () => {
    setBusy(true);
    setError('');
    try {
      const result = await api(token, '/save', {
        mode: actualMode,
        dbNames: chosen,
        titles,
        link,
        recipients,
        days: Number(days),
        attachments: ['link', 'upload'].includes(actualMode) ? attachments : [],
        training,
        trainingAttachment,
        send: true
      });
      await onSaved?.(`Procedure-change communication saved for ${result.results.length} branches.`);
      onClose();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };
  const actualMode = mode === 'pharmace' ? 'cascade' : mode === 'external' ? link.trim() ? 'link' : 'upload' : mode;
  return <div className="sop-cascade-settings" role="dialog" aria-modal="true" aria-label="Procedure-change communication settings">
  <style>{`
    .sop-cascade-settings { position:fixed; inset:0; z-index:10100; background:rgba(15,23,42,.45); display:flex; justify-content:center; align-items:center; padding:28px 12px; overflow:hidden; box-sizing:border-box; color:#262626; font-size:13px; }
    .sop-cascade-settings * { box-sizing:border-box; }
    .sop-cascade-settings .sop-dialog { display:flex; flex-direction:column; width:100%; max-width:1240px; height:calc(100vh - 56px); height:calc(100dvh - 56px); max-height:calc(100dvh - 56px); min-height:0; background:#fff; padding:18px; border-radius:10px; box-shadow:0 10px 40px rgba(0,0,0,.25); overflow:hidden; }
    .sop-cascade-settings .sop-dialog-header { display:flex; align-items:center; justify-content:space-between; gap:12px; flex-shrink:0; margin-bottom:10px; }
    .sop-cascade-settings .sop-dialog-header h3 { font-size:16px; margin:0; font-weight:700; }
    .sop-cascade-settings .sop-dialog-body { display:grid; grid-template-columns:minmax(0,1fr) minmax(0,.9fr) minmax(0,1.2fr); gap:12px; flex:1; min-height:0; padding:10px; border-radius:10px; background:#FAFAFA; overflow:hidden; }
    .sop-cascade-settings .sop-panel { border:1px solid #F0F0F0; border-radius:10px; padding:14px; background:#fff; min-width:0; min-height:0; overflow-y:auto; overflow-x:hidden; overscroll-behavior:contain; overflow-wrap:anywhere; line-height:1.5; }
    .sop-cascade-settings .sop-panel-title { display:flex; align-items:center; justify-content:space-between; gap:8px; margin:0 0 8px; font-size:11px; font-weight:700; letter-spacing:.6px; text-transform:uppercase; color:#5D5D5D; }
    .sop-cascade-settings .sop-panel h4:not(.sop-panel-title) { margin:14px 0 6px; font-size:12px; font-weight:600; color:#434343; }
    .sop-cascade-settings .sop-panel p { margin:8px 0; }
    .sop-cascade-settings .sop-choice { display:block; border:1.5px solid #F0F0F0; border-radius:8px; background:#fff; padding:10px 12px; margin-bottom:8px; cursor:pointer; }
    .sop-cascade-settings .sop-choice.is-selected { border-color:#0E8CFF; background:#E6F7FF; }
    .sop-cascade-settings .sop-choice label { cursor:pointer; }
    .sop-cascade-settings .sop-choice b { font-size:14px; font-weight:700; }
    .sop-cascade-settings .sop-choice-description { color:#5D5D5D; font-size:12px; line-height:1.45; white-space:pre-line; margin-top:4px; }
    .sop-cascade-settings button.sop-action { background:#0E8CFF !important; color:#fff !important; -webkit-text-fill-color:#fff !important; border:0 !important; border-radius:6px; padding:8px 14px; font:inherit; cursor:pointer; text-shadow:none !important; opacity:1; }
    .sop-cascade-settings button.sop-action:disabled { background:#e2e8f0 !important; color:#64748b !important; -webkit-text-fill-color:#64748b !important; cursor:not-allowed; }
    .sop-cascade-settings button.sop-help { width:24px; height:24px; padding:0; border:0; border-radius:50%; background:#389E0D !important; color:white !important; -webkit-text-fill-color:white !important; font-weight:800; flex-shrink:0; cursor:pointer; }
    .sop-cascade-settings .sop-help-text { background:#F6FFED; border:1px solid #B7EB8F; border-radius:8px; color:#237804; font-size:12px; padding:10px 12px; margin:8px 0 10px; }
    .sop-cascade-settings .sop-dialog-footer { display:flex; align-items:center; gap:8px; flex-wrap:wrap; flex-shrink:0; padding-top:12px; background:#fff; }
    .sop-cascade-settings .sop-dialog-footer small { color:#5D5D5D; font-size:11px; }
    .sop-cascade-settings .sop-error { flex-shrink:0; max-height:60px; overflow:auto; color:#b91c1c; margin:0 0 8px; }
    .sop-cascade-settings input:not([type=checkbox]):not([type=radio]), .sop-cascade-settings select { max-width:100%; }
    @media (max-width:760px) { .sop-cascade-settings { padding:12px 8px; } .sop-cascade-settings .sop-dialog { height:calc(100dvh - 24px); max-height:calc(100dvh - 24px); padding:12px; } .sop-cascade-settings .sop-dialog-body { grid-template-columns:minmax(0,1fr); grid-template-rows:repeat(3,minmax(0,1fr)); gap:8px; padding:6px; } .sop-cascade-settings .sop-panel { padding:10px; } }
  `}</style>
  <div className="sop-dialog">
   <header className="sop-dialog-header"><h3>Settings — {row.label}</h3><button className="sop-action" aria-label="Close settings" disabled={busy} onClick={onClose} style={{
          fontSize: 20,
          padding: '4px 10px'
        }}>×</button></header>
   {error && <p role="alert" className="sop-error">{error}</p>}
   {!data ? <p style={{
        flex: 1,
        minHeight: 0
      }}>Loading…</p> : <div className="sop-dialog-body">
   <section className="sop-panel" aria-label="About this item"><h4 className="sop-panel-title">1 · About this item<button className="sop-help" aria-label="Help: about this item" aria-expanded={helpOpen === 'about'} onClick={() => setHelpOpen(helpOpen === 'about' ? '' : 'about')}>?</button></h4>
   {helpOpen === 'about' && <div className="sop-help-text">Record how staff and locums are told about procedure changes and how outstanding reading is followed up. The explanation and evidence for the selected branch are displayed when its cell is opened.</div>}
   <b>{row.label}</b><div style={{
            fontSize: 12,
            color: '#5D5D5D'
          }}>Governance &amp; Risk · Standard Operating Procedures</div><h4>What this is about</h4><p>How affected staff and locums are informed when a pharmacy procedure changes, how they review the new version and how missing acknowledgements are followed up.</p>
   {row.satisfies && <><h4>What satisfies it</h4><p style={{
              whiteSpace: 'pre-line'
            }}>{row.satisfies}</p></>}<h4>Apply to these NI branches</h4>{data.branches.map(b => <label key={b.dbName} style={{
            display: 'block',
            marginBottom: 8
          }}><input type="checkbox" checked={chosen.includes(b.dbName)} onChange={e => setChosen(e.target.checked ? [...chosen, b.dbName] : chosen.filter(n => n !== b.dbName))} />{b.pharmacyName}</label>)}<p style={{
            fontSize: 12
          }}>Only ticked branches are changed. Uploads and selected SOP titles apply to all ticked branches.</p></section>
   <section className="sop-panel" aria-label="Where the evidence comes from"><h4 className="sop-panel-title">2 · Where the evidence comes from<button className="sop-help" aria-label="Help: evidence sources" aria-expanded={helpOpen === 'sources'} onClick={() => setHelpOpen(helpOpen === 'sources' ? '' : 'sources')}>?</button></h4>{helpOpen === 'sources' && <div className="sop-help-text">Choose the process used at the selected branches: Pharmace-managed cascade, particular Pharmace SOPs, external evidence, or a task/email request. Changing this choice does not send anything until you save and apply.</div>}<h4>How do you wish to manage the data?</h4>{[['cascade', 'SOPs cascade managed by Pharmace'], ['sop', 'Select one or more Pharmace SOPs'], ['external', 'Held elsewhere — link or upload SOP and supporting evidence'], ['task', 'Request SOP and evidence — task + email']].map(([value, label]) => <div key={value} className={`sop-choice ${(value === 'external' ? ['link', 'upload'].includes(actualMode) : actualMode === value) ? 'is-selected' : ''}`}><label><input type="radio" name="sop-change-source" checked={value === 'external' ? ['link', 'upload'].includes(actualMode) : actualMode === value} onChange={() => setMode(value)} /> <b>{label}</b></label>{value === 'cascade' && <div className="sop-choice-description">{data.process}</div>}</div>)}{actualMode === 'task' && <TrainingSettings value={training} attachment={trainingAttachment} onChange={setTraining} onAttachment={setTrainingAttachment} />}</section>
   <section className="sop-panel" aria-label="Settings and evidence"><h4 className="sop-panel-title">3 · Settings<button className="sop-help" aria-label="Help: settings and evidence" aria-expanded={helpOpen === 'settings'} onClick={() => setHelpOpen(helpOpen === 'settings' ? '' : 'settings')}>?</button></h4>{helpOpen === 'settings' && <div className="sop-help-text">Complete the details for your chosen source. For external evidence, provide a link or upload the SOP, then add supporting documents. For task/email requests, choose the recipients and review interval.</div>}
    {actualMode === 'cascade' && <p>The cell details show this process and the branch's live SOP documents and reading grid. Maintain uploads, assignments and reminders in the SOP facility.</p>}
    {actualMode === 'sop' && <><p>Choose the procedure(s) describing change communication. Missing documents at a branch will be shown in its details.</p>{data.sops.map(s => <label key={s.title} style={{
              display: 'block',
              marginBottom: 8
            }}><input type="checkbox" checked={titles.includes(s.title)} onChange={e => setTitles(e.target.checked ? [...titles, s.title] : titles.filter(t => t !== s.title))} />{s.title} <small>({s.branches.length} branches)</small></label>)}{!data.sops.length && <p>No Pharmace SOPs are available at these branches.</p>}</>}
    {['link', 'upload'].includes(actualMode) && <label>SOP / communication-process link (or leave blank and upload below)<input type="url" aria-label="SOP process link" value={link} onChange={e => setLink(e.target.value)} placeholder="https://…" style={{
              width: '100%'
            }} /></label>}
    {['upload', 'link'].includes(actualMode) && <><p>Upload the SOP/procedure and supporting records, such as a change notice and staff acknowledgement sheet. These files evidence this item; they do not create or cascade a new Pharmace SOP.</p><label>SOP / procedure document{actualMode === 'upload' ? ' (required)' : ''}<input aria-label="Upload SOP procedure" type="file" multiple disabled={reading || busy} onChange={e => upload(e.target.files, 'sop')} /></label><label style={{
              display: 'block',
              marginTop: 12
            }}>Supporting documentary evidence<input aria-label="Upload supporting evidence" type="file" multiple disabled={reading || busy} onChange={e => upload(e.target.files, 'supporting')} /></label><p style={{
              fontSize: 12
            }}>Up to six documents, 8 MB combined.</p>{attachments.map((f, i) => <div key={i}>{f.filename} <button style={button} onClick={() => setAttachments(attachments.filter((_, j) => j !== i))}>Remove</button></div>)}{data.cells.some(c => c.evidence?.files?.length) && <p>Existing evidence remains visible in cell details. New uploads replace that cell's current file set and retain the previous evidence in history.</p>}</>}
    {actualMode === 'task' && <>{chosen.map(n => <label key={n} style={{
              display: 'block',
              marginBottom: 10
            }}>Recipient — {data.branches.find(b => b.dbName === n)?.pharmacyName}<select aria-label={`Recipient ${n}`} value={recipients[n] || ''} onChange={e => setRecipients({
                ...recipients,
                [n]: e.target.value
              })} style={{
                width: '100%'
              }}><option value="">Choose person…</option>{data.staff.filter(p => p.dbName === n).map(p => <option key={p.staffId} value={p.staffId}>{p.name}</option>)}</select></label>)}<label>Review interval (days)<input type="number" min="1" max="3650" value={days} onChange={e => setDays(e.target.value)} /></label><p>The recipient uploads the procedure and supporting evidence through the staff task or secure email link.</p></>}
   </section>
  </div>}
  <footer className="sop-dialog-footer"><button className="sop-action" disabled={!data || busy || reading || !chosen.length} onClick={save} style={{
          fontWeight: 700
        }}>{busy ? 'Saving…' : 'Save & apply to selected branches'}</button><button className="sop-action" disabled={busy} onClick={onClose}>Cancel</button><small>Only selected branches are changed. Task/email requests are sent when you apply.</small></footer>
 </div></div>;
}
export function SopCascadeDetails({
  row,
  dbName,
  token,
  readOnly = false,
  onChanged
}) {
  const [data, setData] = useState(null),
    [error, setError] = useState(''),
    [configure, setConfigure] = useState(false),
    [refresh, setRefresh] = useState(0);
  useEffect(() => {
    const c = new AbortController();
    setData(null);
    setError('');
    api(token, '/details', {
      dbName
    }, c.signal).then(d => {
      if (!c.signal.aborted) setData(d);
    }).catch(e => {
      if (!c.signal.aborted) setError(e.message);
    });
    return () => c.abort();
  }, [dbName, token, refresh]);
  const cell = data?.cell,
    mode = cell?.sopCascade?.mode,
    docs = cell?.sopDocuments || [];
  const files = cell?.evidence?.files?.length ? cell.evidence.files : cell?.evidence?.fileUrl ? [{
    fileUrl: cell.evidence.fileUrl,
    filename: cell.evidence.filename || 'Supporting evidence'
  }] : [];
  return <section aria-label="SOP change communication details" style={{
    padding: 16,
    color: '#172554'
  }}><h3>{row.label}</h3>{error && <p role="alert">{error}</p>}{!data && !error && <p>Loading…</p>}{data && !cell && <p>This branch's evidence source has not been configured.</p>}
  {cell && <><p><b>Evidence source:</b> {mode === 'cascade' ? 'SOPs cascade managed by Pharmace' : mode === 'sop' ? 'Selected Pharmace SOPs' : mode === 'upload' ? 'Uploaded SOP and documentary evidence' : mode === 'link' ? 'External SOP/process link' : mode === 'task' ? 'Task and email document upload' : mode} · {cell.status}</p>
   {mode === 'cascade' && <div style={{
        background: '#F6FFED',
        border: '1px solid #B7EB8F',
        color: '#237804',
        padding: 14,
        borderRadius: 8
      }}><h4>SOPs cascade managed by Pharmace — process</h4><p style={{
          whiteSpace: 'pre-line',
          lineHeight: 1.6
        }}>{cell.sopCascade.process || data.process}</p></div>}
   {['cascade', 'sop'].includes(mode) && <><h4>SOP documents, versions and review dates</h4>{!docs.length ? <p>No live SOP documents found. Add the documents and assignments in the SOP facility.</p> : <><table style={{
            width: '100%',
            borderCollapse: 'collapse'
          }}><thead><tr><th style={{
                  textAlign: 'left'
                }}>SOP / contents</th><th>Version</th><th>Next review</th></tr></thead><tbody>{docs.map((s, i) => <tr key={s.title + i}><td style={{
                  padding: 8
                }}>{s.fileUrl ? <a href={s.fileUrl} target="_blank" rel="noreferrer">{s.title}</a> : `${s.title} — ${s.held ? 'document file missing' : 'not held at this branch'}`}</td><td style={{
                  textAlign: 'center'
                }}>{s.version || 'Not recorded'}</td><td style={{
                  textAlign: 'center'
                }}>{date(s.expiryDate)}</td></tr>)}</tbody></table><h4>Staff read / unread grid</h4><InspectorSOPStaffGrid pharmacyId={dbName} token={token} asOfDate={new Date().toISOString().slice(0, 10)} sopTitlesFilter={mode === 'sop' ? cell.sopCascade.titles : undefined} /></>}</>}
   {cell.linkUrl && <p><a href={cell.linkUrl} target="_blank" rel="noreferrer">Open SOP / communication-process link</a></p>}
   {files.length > 0 && <><h4>SOP and documentary evidence</h4>{files.map((f, i) => <p key={i}><a href={f.fileUrl} target="_blank" rel="noreferrer">{f.filename || 'View document'}</a>{f.kind === 'sop' ? ' — SOP/procedure' : f.kind === 'supporting' ? ' — supporting evidence' : ''}</p>)}</>}
   {cell.evidence?.at && <p>Evidence received {date(cell.evidence.at)}</p>}{cell.task && <p>Task: {cell.task.status} — {cell.task.recipientName || cell.recipientName}</p>}
   <ComplianceNextDue cell={cell} rowId="el:ni-p13-11" dbName={dbName} token={token} readOnly={readOnly} onChanged={async () => {
        setRefresh(n => n + 1);
        await onChanged?.();
      }} /><TrainingResults cell={cell} />
  </>}
  <button style={button} onClick={() => setRefresh(n => n + 1)}>Refresh evidence</button>{!readOnly && <button style={{
      ...button,
      marginLeft: 8
    }} onClick={() => setConfigure(true)}>Configure this item</button>}
  {configure && <SopCascadeConfig row={row} token={token} onClose={() => setConfigure(false)} onSaved={async () => {
      setRefresh(n => n + 1);
      await onChanged?.();
    }} />}
 </section>;
}
